import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/utils'

type TourSpotlightProps = {
  targetRect: DOMRect | null
  /** Remount + fade-in when the tour step changes (avoids sliding between targets). */
  stepKey: number | string
  padding?: number
  className?: string
}

/**
 * Full-viewport dim with a cut-out around the target element (box-shadow spotlight).
 * Blocks interaction with the rest of the page.
 * Position is applied instantly (no top/left transition) — fade-in only on step change.
 */
export function TourSpotlight({ targetRect, stepKey, padding = 8, className }: TourSpotlightProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return null

  const hasTarget = targetRect !== null && targetRect.width > 0 && targetRect.height > 0
  const top = hasTarget ? targetRect.top - padding : 0
  const left = hasTarget ? targetRect.left - padding : 0
  const width = hasTarget ? targetRect.width + padding * 2 : 0
  const height = hasTarget ? targetRect.height + padding * 2 : 0

  return createPortal(
    <div className={cn('pointer-events-auto fixed inset-0 z-[1100]', className)} aria-hidden>
      {/* Block clicks everywhere */}
      <div className='absolute inset-0' />
      {hasTarget ? (
        <div
          key={`${stepKey}-cutout`}
          data-tour-spotlight='cutout'
          className='animate-in fade-in-0 pointer-events-none absolute rounded-xl border-2 border-[#00AEFF] shadow-[0_0_0_9999px_rgba(15,23,42,0.45)] duration-200'
          style={{
            top,
            left,
            width,
            height,
            // Hard-disable any inherited transition that could interpolate position.
            transition: 'none',
          }}
        />
      ) : (
        <div
          key={`${stepKey}-dim`}
          data-tour-spotlight='dim'
          className='animate-in fade-in-0 absolute inset-0 bg-slate-900/45 duration-200'
          style={{ transition: 'none' }}
        />
      )}
    </div>,
    document.body
  )
}
